/**
 * KioskLock — the "in-app guided access" for tablets running CheckinKiosk,
 * TableMode and KDS.
 *
 * What it does (web-based; no native code required):
 *   • Wake Lock API → keeps the screen on (great for KDS and the entrance kiosk)
 *   • Fullscreen API → tap "Lock" to enter fullscreen (hides browser chrome,
 *     reduces the chance of accidentally swiping out of the app)
 *   • Suppresses right-click, text selection, double-tap zoom + pinch zoom on
 *     non-input elements while locked
 *   • Captures the F11 / Esc / browser-back attempts and routes them through
 *     a PIN-protected exit screen
 *
 * Apple's *real* Guided Access (Settings → Accessibility → Guided Access)
 * is a device-level feature. Once it is enabled on the iPad and triple-clicking
 * the side button armed, *no* web page can leave it. We provide a one-tap
 * "Setup guide" dialog explaining how to turn it on alongside our in-app lock,
 * giving you defense-in-depth.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Lock, LockOpen, Maximize, Minimize, Info, X, Smartphone, Tablet } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/lib/api';

const STORAGE_KEY = 'cg_kiosk_exit_pin';

/* eslint-disable react-hooks/exhaustive-deps */
export function useKioskLock({ enabled = true } = {}) {
  const [locked, setLocked] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const wakeLockRef = useRef(null);

  // Track fullscreen state from the browser itself (covers user pressing Esc)
  useEffect(() => {
    const fsHandler = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', fsHandler);
    return () => document.removeEventListener('fullscreenchange', fsHandler);
  }, []);

  // Acquire / release a screen wake lock while locked
  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return;
    try {
      const wl = await navigator.wakeLock.request('screen');
      wakeLockRef.current = wl;
      wl.addEventListener('release', () => { wakeLockRef.current = null; });
    } catch (e) { /* user agent denied — fine, soft fail */ }
  }, []);
  const releaseWakeLock = useCallback(async () => {
    try { await wakeLockRef.current?.release?.(); } catch (e) { /* noop */ }
    wakeLockRef.current = null;
  }, []);

  // Re-acquire wake lock when the tab becomes visible again
  useEffect(() => {
    if (!locked) return;
    const onVis = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [locked]);

  // Enter / exit lock
  const enter = useCallback(async () => {
    if (!enabled) return;
    setLocked(true);
    try {
      await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' });
    } catch (e) { /* not supported / denied */ }
    await acquireWakeLock();
  }, [enabled]);

  const exit = useCallback(async () => {
    setLocked(false);
    try { if (document.fullscreenElement) await document.exitFullscreen(); } catch (e) { /* noop */ }
    await releaseWakeLock();
  }, []);

  // Suppress common escape gestures while locked
  useEffect(() => {
    if (!locked) return;
    const onContextMenu = (e) => e.preventDefault();
    const onKey = (e) => {
      // Block reload / dev tools while locked
      if ((e.key === 'F5') || ((e.ctrlKey || e.metaKey) && ['r', 'R'].includes(e.key))) {
        e.preventDefault();
        toast.info('Reload disabled while locked');
      }
    };
    const onTouchStart = (e) => {
      // Suppress pinch zoom: 2+ touch points outside form inputs
      if (e.touches.length > 1 && !(e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
        e.preventDefault();
      }
    };
    let lastTap = 0;
    const onTouchEnd = (e) => {
      const now = Date.now();
      if (now - lastTap < 300 && !(e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) {
        e.preventDefault(); // kill double-tap zoom
      }
      lastTap = now;
    };
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('touchstart', onTouchStart, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: false });
    document.body.classList.add('kiosk-locked');
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
      document.body.classList.remove('kiosk-locked');
    };
  }, [locked]);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen?.({ navigationUI: 'hide' });
    } catch (e) { toast.error('Fullscreen not supported on this device'); }
  }, []);

  return { locked, fullscreen, enter, exit, toggleFullscreen };
}

/**
 * Floating control bar shown in the corner of a kiosk surface.
 * Operators tap "Lock" to enter — and need an exit PIN to leave.
 *
 * The exit PIN is now verified against the backend (set by an admin
 * under "Policies & Settings → Kiosk Manager Exit PIN") so it survives
 * device wipes and is consistent across every kiosk. Legacy local
 * fallback PIN is still supported when the backend hasn't been
 * configured yet — see `tryExit()`.
 */
export function KioskLockBar({ title = 'Kiosk', defaultExitPin = '42424', position = 'top-right' }) {
  const { locked, fullscreen, enter, exit, toggleFullscreen } = useKioskLock();
  const [exitDialog, setExitDialog] = useState(false);
  const [helpDialog, setHelpDialog] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const tryExit = async () => {
    if (!/^\d{5}$/.test(pinInput)) {
      setError('Enter the 5-digit manager exit PIN.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/staff/kiosk/verify-exit-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pinInput }),
      });
      if (res.ok) {
        setExitDialog(false);
        setPinInput('');
        setError('');
        await exit();
        toast.success('Kiosk unlocked');
        return;
      }
      // Backend rejected — fall back to legacy local PIN if no PIN has been
      // configured server-side (HTTP 400 "No manager exit PIN configured").
      let detail = '';
      try { detail = (await res.json())?.detail || ''; } catch (e) { /* noop */ }
      if (res.status === 400 && /no manager exit pin/i.test(detail)) {
        const legacyPin = localStorage.getItem(STORAGE_KEY) || defaultExitPin;
        if (pinInput === legacyPin) {
          setExitDialog(false);
          setPinInput('');
          setError('');
          await exit();
          toast.success('Kiosk unlocked (legacy PIN)');
          return;
        }
        setError('No manager exit PIN configured. Ask an admin to set one in Policies & Settings.');
        setPinInput('');
        return;
      }
      setError(detail || 'Incorrect PIN. Try again or call a manager.');
      setPinInput('');
    } catch (e) {
      setError('Could not reach server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const posCls = {
    'top-right':    'top-3 right-3',
    'top-left':     'top-3 left-3',
    'bottom-right': 'bottom-3 right-3',
    'bottom-left':  'bottom-3 left-3',
  }[position] || 'top-3 right-3';

  return (
    <>
      <div className={`fixed z-50 flex items-center gap-1 rounded-full border border-white/10 bg-black/40 px-1.5 py-1 backdrop-blur-md ${posCls}`} data-testid="kiosk-lock-bar">
        {!locked ? (
          <>
            <Button data-testid="kiosk-lock-enter" size="sm" variant="ghost" className="h-7 gap-1 text-[11px] text-white hover:bg-white/10" onClick={enter}>
              <Lock className="h-3.5 w-3.5" /> Lock {title}
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/10" onClick={() => setHelpDialog(true)} aria-label="Setup guide" data-testid="kiosk-lock-help">
              <Info className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <>
            <span className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-300">
              <Lock className="h-3.5 w-3.5" /> Locked
            </span>
            <Button data-testid="kiosk-lock-fs" size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-white/10" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
              {fullscreen ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
            </Button>
            <Button data-testid="kiosk-lock-exit" size="sm" variant="ghost" className="h-7 gap-1 text-[11px] text-white hover:bg-white/10" onClick={() => setExitDialog(true)}>
              <LockOpen className="h-3.5 w-3.5" /> Exit
            </Button>
          </>
        )}
      </div>

      {/* Exit-PIN dialog */}
      <Dialog open={exitDialog} onOpenChange={(o) => { if (!o) { setExitDialog(false); setPinInput(''); setError(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Unlock kiosk</DialogTitle>
            <DialogDescription>Enter the manager exit PIN to leave kiosk mode.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input
              data-testid="kiosk-lock-exit-pin"
              type="password"
              inputMode="numeric"
              maxLength={5}
              autoFocus
              value={pinInput}
              onChange={(e) => { setPinInput(e.target.value.replace(/\D/g, '').slice(0, 5)); setError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') tryExit(); }}
              className="h-12 w-full rounded-md border border-input bg-background px-3 text-center font-display text-2xl tracking-[0.5em]"
              placeholder="•••••"
            />
            {error && <p className="text-center text-xs text-rose-600">{error}</p>}
            <p className="text-center text-[10px] text-muted-foreground">
              Set the manager exit PIN under <span className="font-semibold">Admin &rarr; Policies &amp; Settings</span>.
            </p>
            <Button data-testid="kiosk-lock-exit-submit" className="w-full" onClick={tryExit} disabled={busy || pinInput.length !== 5}>
              {busy ? 'Verifying\u2026' : 'Unlock'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help / setup-guide dialog */}
      <KioskSetupHelpDialog open={helpDialog} onClose={() => setHelpDialog(false)} />
    </>
  );
}

function KioskSetupHelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Lock this device to City Grill (Guided Access)</DialogTitle>
          <DialogDescription>Two layers of protection: our in-app lock (here) + your tablet's native Guided Access. Together it's iron-clad.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-2 flex items-center gap-2 font-semibold"><Tablet className="h-4 w-4 text-primary" /> iPad — Guided Access</p>
            <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal pl-4">
              <li>Settings → <strong>Accessibility</strong> → <strong>Guided Access</strong> → turn ON.</li>
              <li>Set a <strong>passcode</strong> (separate from device passcode).</li>
              <li>Open Safari → navigate to this kiosk URL.</li>
              <li>Triple-click the <strong>side / home button</strong> → Guided Access starts.</li>
              <li>To leave: triple-click + enter your Guided Access passcode.</li>
            </ol>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-2 flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4 text-primary" /> Android — Pin app</p>
            <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal pl-4">
              <li>Settings → <strong>Security</strong> → <strong>Advanced</strong> → <strong>App pinning</strong> → ON.</li>
              <li>Open this URL in Chrome → tap menu → <strong>Install app / Add to home screen</strong>.</li>
              <li>Open the recents view → tap the City Grill thumbnail icon → <strong>Pin</strong>.</li>
              <li>To leave: hold Back + Recents.</li>
            </ol>
          </div>
        </div>
        <p className="mt-3 rounded-md bg-secondary/60 p-3 text-xs text-muted-foreground">
          <strong>Pro tip:</strong> Add this kiosk URL to your home screen first ("Add to Home Screen"). It opens fullscreen as a standalone app, which combined with Guided Access gives the cleanest experience.
        </p>
        <div className="flex justify-end">
          <Button variant="secondary" onClick={onClose}><X className="mr-1 h-4 w-4" /> Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
